import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Read-only list of driver_assignment (seller-module) — driver/company/line/plate/date/postedAt/source,
// already resolved server-side to nickname/company/sellerName (never raw uids). Wired into the
// sidebar (Home.js navigationItems, "sales" mode) per Vector's request.
// + ทะเบียนคนขับ (driver-registry) ด้านบนของหน้า — บัญชี/แอดมินเพิ่ม/ถอดคนขับที่โผล่ในแอป IMD (2026-10-05).

const headerStyle = { background: '#f8f9fa', fontWeight: 700, borderBottom: '2px solid #E5E5E5', padding: '8px 12px', fontSize: '13px', color: '#495057', textAlign: 'left' };
const rowStyle = { borderBottom: '1px solid #E5E5E5', padding: '6px 12px' };

const fmtTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (x) => String(x).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DriverAssignmentList = () => {
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // ทะเบียนคนขับ (Vector 2026-10-05): รายชื่อในช่อง "ใครขับรอบนี้" ของแอป IMD มีเฉพาะคนที่อยู่ที่นี่
    const [registry, setRegistry] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [pickUid, setPickUid] = useState('');
    const [registryMsg, setRegistryMsg] = useState('');
    const [registryBusy, setRegistryBusy] = useState(false);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) { navigate('/login'); return null; }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const fetchAssignments = useCallback(async () => {
        const token = checkToken();
        if (!token) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/seller/api/driver-assignments`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) {
                setError(res.status === 401 || res.status === 403
                    ? 'ไม่มีสิทธิ์เข้าถึงข้อมูลนี้'
                    : `โหลดข้อมูลไม่สำเร็จ (HTTP ${res.status})`);
                setAssignments([]);
                return;
            }
            const data = await res.json();
            setAssignments(Array.isArray(data) ? data : []);
        } catch (e) {
            setError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
            setAssignments([]);
        } finally {
            setLoading(false);
        }
    }, [checkToken]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    const fetchRegistry = useCallback(async () => {
        const token = checkToken();
        if (!token) return;
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [r1, r2] = await Promise.all([
                fetch(`${API_BASE_URL}/seller/api/driver-registry`, { headers }),
                fetch(`${API_BASE_URL}/seller/api/driver-registry/candidates`, { headers }),
            ]);
            if (!r1.ok || !r2.ok) {
                setRegistryMsg(r1.status === 401 || r1.status === 403
                    ? 'ไม่มีสิทธิ์ดูทะเบียนคนขับ'
                    : `โหลดทะเบียนคนขับไม่สำเร็จ (HTTP ${r1.ok ? r2.status : r1.status})`);
                return;
            }
            const list = await r1.json();
            const cand = await r2.json();
            setRegistry(Array.isArray(list) ? list : []);
            setCandidates(Array.isArray(cand) ? cand : []);
            setRegistryMsg('');
        } catch (e) {
            setRegistryMsg('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
        }
    }, [checkToken]);

    useEffect(() => {
        fetchRegistry();
    }, [fetchRegistry]);

    const registryCall = async (method, path, body, okText) => {
        const token = checkToken();
        if (!token || registryBusy) return;
        setRegistryBusy(true);
        setRegistryMsg('');
        try {
            const res = await fetch(`${API_BASE_URL}/seller/api/driver-registry${path}`, {
                method,
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: body ? JSON.stringify(body) : undefined,
            });
            if (!res.ok) {
                setRegistryMsg(res.status === 401 || res.status === 403
                    ? 'ไม่มีสิทธิ์แก้ทะเบียนคนขับ'
                    : `ทำรายการไม่สำเร็จ (HTTP ${res.status})`);
                return;
            }
            setPickUid('');
            await fetchRegistry();
            setRegistryMsg(okText);
        } catch (e) {
            setRegistryMsg('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
        } finally {
            setRegistryBusy(false);
        }
    };

    const addDriver = () => {
        if (!pickUid) return;
        registryCall('POST', '', { employeeId: pickUid }, 'เพิ่มคนขับในทะเบียนแล้ว');
    };

    const removeDriver = (d) => {
        if (!window.confirm(`ถอด "${d.nickname || d.employeeUid}" ออกจากทะเบียนคนขับ?\n(รอบเก่ายังแสดงชื่อเหมือนเดิม)`)) return;
        registryCall('DELETE', `/${encodeURIComponent(d.employeeUid)}`, null, 'ถอดออกจากทะเบียนแล้ว');
    };

    return (
        <div className="btc-container" style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0 }}>รายการมอบหมายคนขับ (Driver Assignments)</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button className="btc-btn" style={{ background: '#6B6B6B', padding: '4px 10px', fontSize: '13px' }} onClick={() => navigate(-1)}>← กลับ</button>
                    <button className="btc-btn" style={{ background: '#E60012', color: '#fff', padding: '4px 10px', fontSize: '13px' }} onClick={fetchAssignments}>รีเฟรช</button>
                </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #E5E5E5', borderRadius: '6px', padding: '12px', marginBottom: '16px' }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '15px' }}>ทะเบียนคนขับ</h3>
                <p style={{ margin: '0 0 8px 0', color: '#6B6B6B', fontSize: '12px' }}>
                    รายชื่อในช่อง "ใครขับรอบนี้" ของแอป IMD มีเฉพาะคนที่อยู่ในทะเบียนนี้ · พิมพ์ชื่อใหม่ในแอปจะลงทะเบียนให้อัตโนมัติ
                </p>
                {registryMsg && <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#495057' }}>{registryMsg}</p>}
                {registry.length === 0 ? (
                    <p style={{ color: '#6B6B6B', fontSize: '13px' }}>ยังไม่มีคนขับในทะเบียน</p>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                        {registry.map(d => (
                            <span key={d.employeeUid} style={{ border: '1px solid #E5E5E5', borderRadius: '14px', padding: '3px 10px', fontSize: '13px', background: '#f8f9fa' }}>
                                {d.nickname || d.employeeUid}{d.company ? ` (${d.company})` : ''}
                                <button style={{ marginLeft: '8px', border: 'none', background: 'none', color: '#E60012', cursor: 'pointer' }} disabled={registryBusy} onClick={() => removeDriver(d)}>ถอด</button>
                            </span>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={pickUid} onChange={e => setPickUid(e.target.value)} style={{ padding: '4px', fontSize: '13px' }}>
                        <option value="">— เลือกพนักงานที่จะเพิ่มเป็นคนขับ —</option>
                        {candidates.map(c => (
                            <option key={c.uid} value={c.uid}>{c.nickname}{c.company ? ` (${c.company})` : ''}</option>
                        ))}
                    </select>
                    <button className="btc-btn" style={{ background: '#E60012', color: '#fff', padding: '4px 10px', fontSize: '13px' }} disabled={!pickUid || registryBusy} onClick={addDriver}>เพิ่มเป็นคนขับ</button>
                </div>
            </div>

            {loading && <p style={{ color: '#6B6B6B' }}>กำลังโหลด...</p>}
            {!loading && error && <p style={{ color: '#E60012' }}>{error}</p>}
            {!loading && !error && assignments.length === 0 && <p style={{ color: '#6B6B6B' }}>ไม่มีข้อมูล</p>}

            {!loading && !error && assignments.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: '#fff' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}>คนขับ</th>
                                <th style={headerStyle}>บริษัท</th>
                                <th style={headerStyle}>สาย</th>
                                <th style={headerStyle}>ทะเบียนรถ</th>
                                <th style={headerStyle}>วันที่</th>
                                <th style={headerStyle}>เวลาโพสต์จริง</th>
                                <th style={headerStyle}>Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assignments.map(a => (
                                <tr key={a.id}>
                                    <td style={rowStyle}>{a.employeeNickname || a.employeeId}</td>
                                    <td style={rowStyle}>{a.employeeCompany || '-'}</td>
                                    <td style={rowStyle}>{a.sellerName || a.sellerId || '-'}</td>
                                    <td style={rowStyle}>{a.vehicleId || '-'}</td>
                                    <td style={rowStyle}>{a.assignedDate || '-'}</td>
                                    <td style={rowStyle}>{fmtTime(a.postedAt)}</td>
                                    <td style={rowStyle}>{a.source || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DriverAssignmentList;
